import { Log } from "src/types.g";
import { ObjectType } from "src/helpers/logHelper";

enum Action {
  I = "I",
  U = "U",
  D = "D",
}

export const actionsText = {
  I: "create",
  U: "update",
  D: "delete",
};

export const actionsLabel = {
  I: "Created",
  U: "Updated",
  D: "Deleted",
  R: "Renamed",
  t: "Published",
};

interface GetLogActionDetailsParams {
  logDetailData: any;
  log: Partial<Log>;
  previousData?: any;
  changedData?: any;
}

export const getLogActionDetails = ({
  logDetailData,
  log,
  previousData,
  changedData,
}: GetLogActionDetailsParams) => {
  if (log.action === Action.I) {
    if (
      log.objectType === ObjectType.AppInstall ||
      log.objectType === ObjectType.AppInstance
    ) {
      return getAppActionDetails(log.objectType, log.action, log);
    }

    if (log.objectType === ObjectType.Favorite) {
      return getFavoriteActionDetails(log.action, logDetailData);
    }

    const formattedObjectType = (log.objectType || "").replace(/_/g, " ");
    return `${actionsLabel.I} ${formattedObjectType}`;
  }

  if (log.action === Action.D) {
    if (
      log.objectType === ObjectType.AppInstall ||
      log.objectType === ObjectType.AppInstance
    ) {
      return getAppActionDetails(log.objectType, log.action, log);
    }

    if (log.objectType === ObjectType.Favorite) {
      return getFavoriteActionDetails(log.action, logDetailData);
    }

    const formattedObjectType = (log.objectType || "").replace(/_/g, " ");
    return `${actionsLabel.D} ${formattedObjectType}`;
  }

  if (log.action === Action.U) {
    if (logDetailData?.name) {
      return `${actionsLabel.R} "${log.previousFields?.name ?? ""}" to "${logDetailData?.name}"`;
    }

    if (logDetailData.is_published) {
      return `${actionsLabel.t} ${log.objectType}`;
    }

    if (log.objectType === ObjectType.Screen) {
      return getScreenUpdateActionDetails(
        logDetailData,
        previousData,
        changedData,
      );
    }

    if (
      log.objectType === ObjectType.AppInstall ||
      log.objectType === ObjectType.AppInstance
    ) {
      return getAppActionDetails(log.objectType, log.action, log);
    }

    return `${actionsLabel.U} ${log.objectType}`;
  }

  return null;
};

const getScreenUpdateActionDetails = (
  logDetailData,
  previousData,
  changedData,
): string => {
  if (logDetailData?.content) {
    return `Changed content from "${previousData}" to "${changedData}"`;
  }

  if ("cast_id" in logDetailData && logDetailData.cast_id !== null) {
    return `Started casting content to ${ObjectType.Screen}`;
  }

  if ("cast_id" in logDetailData && logDetailData.cast_id === null) {
    return `Stopped casting content to ${ObjectType.Screen}.`;
  }

  if (
    "screen_group_id" in logDetailData &&
    logDetailData.screen_group_id !== null
  ) {
    return `Added screen to group`;
  }

  if (logDetailData?.device_id) {
    return `Replaced device`;
  }

  return `${actionsLabel.U} ${ObjectType.Screen}`;
};

const getAppActionDetails = (
  objectType: ObjectType,
  action: Action,
  log: Partial<Log>,
): string => {
  if (objectType === ObjectType.AppInstall) {
    if (action === Action.I) {
      return `Installed`;
    }

    if (action === Action.D) {
      return `Uninstalled`;
    }

    return actionsLabel[action];
  }

  if (objectType === ObjectType.AppInstance) {
    return `${actionsLabel[action]} instance${log.objectName ? ` "${log.objectName}"` : ""}`;
  }

  return `${actionsLabel[action]} ${objectType}`;
};

type FavoriteLogDetailData = {
  file_id: string | null;
  link_id: string | null;
  site_id: string | null;
  space_id: string | null;
  folder_id: string | null;
  playlist_id: string | null;
};

const getFavoriteActionDetails = (
  action: Action,
  logDetailData: FavoriteLogDetailData,
): string => {
  const contentTypeMap = {
    file_id: "file",
    link_id: "link",
    site_id: "dashboard",
    folder_id: "folder",
    playlist_id: "playlist",
  };

  const contentType =
    Object.entries(contentTypeMap).find(
      ([key]) => logDetailData[key as keyof FavoriteLogDetailData],
    )?.[1] || "content";

  const actionText = action === Action.I ? "Added" : "Removed";
  const preposition = action === Action.I ? "to" : "from";
  return `${actionText} ${contentType} ${preposition} favorites`;
};
